import { ReactNode, useState, useRef, useEffect } from 'react';

interface MenuItem {
  icon: ReactNode;
  text: string;
  onClick: (e: React.MouseEvent) => void;
}

interface DropdownMenuWithHeroButtonProps {
  buttonText?: string;
  items: MenuItem[];
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const DropdownMenuWithHeroButton: React.FC<DropdownMenuWithHeroButtonProps> = ({
  buttonText,
  items,
  leftIcon,
  rightIcon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative ">
      <button
        type="button"
        className="relative w-[100px] w-[100px] px-3 py-3 rounded-full text-white font-semibold border-none ring-2 ring-transparent overflow-hidden group transition-all duration-300 ease-in-out flex items-center justify-center"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setIsOpen(prev => !prev);
        }}
      >
        <span className="absolute inset-0 bg-gradient-to-r from-custom-teal via-custom-purple to-custom-dark-purple transition-all duration-300 ease-in-out group-hover:bg-gradient-custom-hover" />
        <div className="relative z-10 flex items-center justify-between w-full">
          {leftIcon && <span className="text-[10px]">{leftIcon}</span>}
          <span className="font-bold text-sm md:text-sm">{buttonText}</span>
          {rightIcon && <span className="text-[10px]">{rightIcon}</span>}
        </div>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 min-w-[16rem] whitespace-nowrap bg-white border border-[#D9D8E1] rounded-lg shadow-xl overflow-hidden z-50"
          ref={dropdownRef}
        >
          <div className="py-2 w-auto">
            {items.map((item, index) => (
              <div
                key={index}
                className="px-4 py-2 flex items-center space-x-3 text-[#48455B] hover:bg-gray-200 cursor-pointer transition-colors duration-150 mx-2"
                onClick={(e) => {
                  item.onClick(e);
                  setIsOpen(false);
                }}
              >
                {item.icon}
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownMenuWithHeroButton;
